import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Lock, ArrowLeft, Clock, Sparkles, Crown, ArrowRight } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button, Badge, Spinner, Card } from "@/components/kit";

const CAT_META = {
  sales: { label: "Sales Tips", color: "sky" },
  case: { label: "Studi Kasus", color: "violet" },
  growth: { label: "Growth Hack UMKM", color: "emerald" },
};

export default function CommunityArticle() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [tip, setTip] = useState(null);
  const [allTips, setAllTips] = useState([]);

  useEffect(() => {
    setTip(null);
    api.get(`/community/tips/${slug}`).then((r) => setTip(r.data.tip)).catch((e) => toast.error(apiErr(e)));
    api.get("/community/tips").then((r) => setAllTips(r.data.tips || [])).catch(() => {});
  }, [slug]);

  if (!tip) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7" /></div>;

  const cat = CAT_META[tip.category] || CAT_META.sales;
  const isLocked = tip.locked;
  const related = allTips.filter((t) => t.category === tip.category && t.slug !== tip.slug).slice(0, 3);

  return (
    <div className="-mx-6 -mt-6">{/* break out of AppLayout's inner padding for full-bleed cover */}

      {/* Full-bleed cover with Ken-Burns animation */}
      <div className="relative h-[55vh] min-h-[420px] max-h-[640px] overflow-hidden">
        <div
          data-testid="article-cover"
          className="absolute inset-0 bg-cover bg-center animate-kenburns"
          style={{ backgroundImage: `url(${tip.cover_image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/30 to-slate-900/95" />

        {/* Back button */}
        <button onClick={() => nav("/app/community")} data-testid="article-back"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white bg-black/30 hover:bg-black/50 backdrop-blur rounded-full px-3.5 py-2 transition z-10">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </button>

        {/* Title block at bottom */}
        <div className="absolute inset-x-0 bottom-0 z-10 px-6 md:px-14 pb-10 md:pb-14 text-white max-w-5xl mx-auto">
          <Badge color={cat.color} className="bg-white/15 text-white border-white/30 backdrop-blur">
            <Sparkles className="w-3 h-3" /> {cat.label}
            {tip.is_featured && <span className="ml-2">· ★ Featured</span>}
          </Badge>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold mt-4 leading-[1.1] max-w-3xl serif-cinematic" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {tip.title}
          </h1>
          <p className="text-base md:text-xl text-slate-200 mt-4 max-w-2xl leading-relaxed">{tip.excerpt}</p>
          <div className="flex items-center gap-4 mt-5 text-sm text-slate-300">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {tip.read_time} menit baca</span>
            <span>· Tim RESADA</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <article className="bg-white px-6 md:px-14 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          {isLocked ? (
            <LockedOverlay tip={tip} />
          ) : (
            <div className="prose prose-slate max-w-none lg:columns-2 lg:gap-12 prose-headings:font-display prose-headings:break-after-avoid prose-p:break-inside-avoid-column prose-li:break-inside-avoid prose-table:break-inside-avoid prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-3 prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2 prose-h2:text-slate-900 prose-h3:text-slate-800 prose-p:text-slate-700 prose-p:leading-relaxed prose-li:text-slate-700 prose-strong:text-slate-900 prose-a:text-sky-600 prose-blockquote:border-l-sky-400 prose-blockquote:bg-sky-50/50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-table:text-sm prose-th:bg-slate-100 prose-th:p-2 prose-td:p-2 prose-td:border prose-td:border-slate-200 prose-img:rounded-xl"
              data-testid="article-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{tip.body_md}</ReactMarkdown>
            </div>
          )}

          {/* Tags */}
          {(tip.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-12 pt-6 border-t border-slate-100">
              {tip.tags.map((t) => (
                <span key={t} className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">#{t}</span>
              ))}
            </div>
          )}

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-14" data-testid="article-related">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-500" /> Lanjut Baca · {cat.label}
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/app/community/${r.slug}`}
                    className="group rounded-2xl overflow-hidden bg-slate-50 hover:shadow-md transition">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={r.cover_image} alt={r.title}
                        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${r.locked ? "grayscale" : ""}`} />
                    </div>
                    <div className="p-4">
                      <p className="font-semibold text-sm text-slate-800 line-clamp-2 group-hover:text-sky-600 transition">{r.title}</p>
                      <p className="text-xs text-slate-400 mt-1.5 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {r.read_time} min
                        {r.locked && <Lock className="w-3 h-3 ml-1 text-amber-500" />}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}

function LockedOverlay({ tip }) {
  const nav = useNavigate();
  return (
    <div className="relative max-w-3xl mx-auto" data-testid="article-locked">
      {/* Faded preview text behind the card so the paywall feels organic */}
      <div className="absolute inset-0 -z-10 [filter:blur(6px)] opacity-40 select-none pointer-events-none">
        <p className="text-slate-600 leading-relaxed mb-4">{tip.excerpt}</p>
        <p className="text-slate-500 leading-relaxed">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Quisque facilisis,
          orci eget tincidunt suscipit, justo nibh euismod augue, ac luctus elit lorem
          a tortor. Phasellus eu tellus a leo placerat fermentum. Sed in lacus eu enim
          rutrum tristique. Aenean ac elit non ipsum eleifend porta...
        </p>
      </div>

      <Card className="p-8 md:p-10 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border-2 border-amber-200">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center mx-auto shadow-lg">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h3 className="font-display font-extrabold text-2xl md:text-3xl text-slate-900 mt-5">
            Artikel ini khusus untuk anggota <span className="text-amber-600">Starter +</span>
          </h3>
          <p className="text-slate-600 mt-3 max-w-lg mx-auto leading-relaxed">
            Buka akses penuh ke <b>{43}+ tips</b> sales, studi kasus customer nyata, dan growth hack UMKM
            yang membantu freelancer & agency closing miliaran rupiah.
          </p>

          <div className="grid sm:grid-cols-3 gap-3 mt-7 max-w-md mx-auto text-sm">
            {[
              ["43+", "artikel terkurasi"],
              ["1.000", "kredit/bulan"],
              ["WA + Email", "blast"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl bg-white/70 backdrop-blur p-3">
                <p className="font-display font-extrabold text-xl text-amber-700">{k}</p>
                <p className="text-xs text-slate-500">{v}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button data-testid="article-upgrade-btn" onClick={() => nav("/app/billing")} size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 shadow-lg">
              <Crown className="w-4 h-4" /> Upgrade ke Starter — Rp 99rb/bulan
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Link to="/app/community" className="text-sm font-semibold text-slate-600 hover:text-slate-900">
              ← Lihat artikel gratis
            </Link>
          </div>

          <p className="text-xs text-slate-400 mt-4">Cancel kapan saja · Tidak perlu kartu kredit</p>
        </div>
      </Card>
    </div>
  );
}
